import React, { useState, useMemo, useEffect, useRef } from "react";
import "./App.css";
import ProjectSchedule from "./components/ProjectSchedule";
import ProjectEditModal from "./components/ProjectEditModal";
import BackupModal from "./components/BackupModal";
import LoginPage from "./components/LoginPage";
import { getNextProjectId } from "./utils/scheduleCalculations";
import { SAMPLE_CATEGORIES, createDefaultProject } from "./models/projectModel";
import { Schedule } from "./models/Schedule";
import { Project } from "./models/Project";
import { Category } from "./models/Category";
import { getCategoryLevelColor } from "./config/categoryColors";
import { readCSVFile } from "./utils/csvExport";
import { saveSchedule, serializeCategories, loadSchedule } from "./services/api";
import { logout } from "./services/authService";
import { useAuth } from "./context/AuthContext";
import {
  getConfiguredMonthlyCapacity,
  resetConfiguredMonthlyCapacity,
  setConfiguredMonthlyCapacity,
} from "./config/availableEffort";
import {
  WorkCategory,
  Status,
  EffortBreakdown,
  ClientProject,
  CategoryTimingShiftPayload,
  ProjectTask,
  TaskStatus,
} from "./types";

const App: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [pendingAddCategoryId, setPendingAddCategoryId] = useState<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState<boolean>(false);
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);
  const [capacityConfigVersion, setCapacityConfigVersion] =
    useState<number>(0);

  const {
    user: authUser,
    loading: authLoading,
    setUser: setAuthUser,
    canEdit,
  } = useAuth();

  const formatRole = (role: "default" | "viewer" | "admin") =>
    role.charAt(0).toUpperCase() + role.slice(1);

  // Auto-save debounce timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Create Schedule instance from categories
  const schedule = useMemo(() => new Schedule(categories), [categories]);

  // Load "default" schedule from DB on startup
  useEffect(() => {
    if (!authUser) return;
    const loadDefault = async () => {
      try {
        const data = await loadSchedule("default");
        const reconstructed = reconstructCategories(data.categories);
        setCategories(reconstructed);
        setClientProjects(data.clientProjects || []);
        if (data.capacity) {
          setConfiguredMonthlyCapacity(data.capacity);
        } else {
          resetConfiguredMonthlyCapacity();
        }
        setCapacityConfigVersion((prev) => prev + 1);
      } catch {
        // No saved schedule yet - start with sample data
        setCategories(SAMPLE_CATEGORIES);
        setClientProjects([]);
        resetConfiguredMonthlyCapacity();
        setCapacityConfigVersion((prev) => prev + 1);
      } finally {
        setIsLoaded(true);
      }
    };
    loadDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const reconstructCategories = (data: any[]): Category[] =>
    data.map((catData) => {
      const projects = (catData.projects || []).map((p: any) => Project.build(p));
      const subcategories = reconstructCategories(catData.subcategories || []);
      return new Category({
        id: catData.id,
        name: catData.name,
        color: catData.color,
        projects,
        subcategories,
        parentId: catData.parentId,
        level: catData.level,
      });
    });

  // Auto-save to database whenever categories change (with debouncing)
  useEffect(() => {
    // Don't save until the initial load from DB is complete
    if (!isLoaded) return;
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer to save after 1 second of inactivity
    autoSaveTimerRef.current = setTimeout(() => {
      const saveToDb = async () => {
        try {
          await saveSchedule(
            "default",
            serializeCategories(categories),
            getConfiguredMonthlyCapacity(),
            clientProjects,
          );
          console.log("Auto-saved to database");
        } catch (error) {
          console.error("Auto-save failed:", error);
          // Silently fail - don't interrupt user experience
        }
      };
      saveToDb();
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [categories, clientProjects, isLoaded, capacityConfigVersion]);

  const handleCapacityConfigChange = () => {
    setCapacityConfigVersion((prev) => prev + 1);
  };

  const handleProjectClick = (project: Project): void => {
    setPendingAddCategoryId(null);
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setPendingAddCategoryId(null);
    setIsModalOpen(false);
  };

  const parseTaskWeekDate = (isoDate: string): Date | null => {
    const [year, month, day] = isoDate.split("-").map(Number);
    if (!year || !month || !day) return null;
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  };

  const toTaskWeekIso = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const dayDelta = (from?: Date, to?: Date): number | null => {
    if (!from || !to) return null;
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.round((to.getTime() - from.getTime()) / msPerDay);
  };

  const shiftTasksForDateChange = (
    previousProject: Project,
    nextProject: Project,
    nextStartDate?: Date,
    nextEndDate?: Date,
  ): ProjectTask[] => {
    const nextTasks = nextProject.tasks || [];
    if (nextTasks.length === 0) return nextTasks;

    const startDelta = dayDelta(
      previousProject.startDate,
      nextStartDate ?? nextProject.startDate,
    );
    const endDelta = dayDelta(
      previousProject.endDate,
      nextEndDate ?? nextProject.endDate,
    );

    const hasDateChange = (startDelta ?? 0) !== 0 || (endDelta ?? 0) !== 0;
    if (!hasDateChange) return nextTasks;

    if (nextProject.status === Status.PLANNED) {
      const rawDeltaDays = startDelta ?? endDelta ?? 0;
      const deltaWeeks = Math.round(rawDeltaDays / 7);
      const deltaDays = deltaWeeks * 7;
      if (deltaDays === 0) return nextTasks;

      return nextTasks.map((task) => {
        if (task.status === TaskStatus.DONE) return task;
        const start = parseTaskWeekDate(task.weekStart);
        if (!start) return task;
        const shiftedStart = addDays(start, deltaDays);
        return {
          ...task,
          weekStart: toTaskWeekIso(shiftedStart),
        };
      });
    }

    if (nextProject.status === Status.IN_PROGRESS) {
      const rawDeltaDays = startDelta ?? 0;
      const deltaWeeks = Math.round(rawDeltaDays / 7);
      const deltaDays = deltaWeeks * 7;
      if (deltaDays === 0) return nextTasks;

      return nextTasks.map((task) => {
        if (task.status === TaskStatus.DONE) return task;
        const start = parseTaskWeekDate(task.weekStart);
        if (!start) return task;

        const safeWeeks = Math.max(1, task.weekCount || 1);
        const endInclusive = addDays(start, (safeWeeks - 1) * 7);
        const shiftedStart = addDays(start, deltaDays);
        const newWeekCount = Math.max(
          1,
          Math.round(
            (endInclusive.getTime() - shiftedStart.getTime()) /
              (1000 * 60 * 60 * 24 * 7),
          ) + 1,
        );

        return {
          ...task,
          weekStart: toTaskWeekIso(shiftedStart),
          weekCount: newWeekCount,
        };
      });
    }

    return nextTasks;
  };

  const handleSaveProject = (
    updatedProject: Project,
    options?: { keepModalOpen?: boolean }
  ): void => {
    setCategories((prevCategories) => {
      // Recursively update categories
      const updateCategoryTree = (cat: Category): Category => {
        // Check if this category contains the project
        const projectExists = cat
          .getProjects()
          .some((p) => p.id === updatedProject.id);

        if (projectExists) {
          const existingProject = cat
            .getProjects()
            .find((p) => p.id === updatedProject.id);
          const adjustedTasks = existingProject
            ? shiftTasksForDateChange(existingProject, updatedProject)
            : updatedProject.tasks;

          const projectToPersist = updatedProject.update({
            tasks: adjustedTasks,
          });
          projectToPersist.status = updatedProject.status;

          // Update the project in this category
          const updatedProjects = cat
            .getProjects()
            .map((p) => (p.id === updatedProject.id ? projectToPersist : p));
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: updatedProjects,
            subcategories: cat.getSubcategories(),
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        // Recursively check subcategories
        const hasUpdatedSubcategory = cat
          .getSubcategories()
          .some((sub) =>
            sub.getAllProjects().some((p) => p.id === updatedProject.id)
          );

        if (hasUpdatedSubcategory) {
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: cat.getSubcategories().map(updateCategoryTree),
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        return cat;
      };

      const newCategories = prevCategories.map(updateCategoryTree);

      // If project doesn't exist, add to selected category
      if (updatedProject.id === null) {
        const allProjects = prevCategories.flatMap((c) => c.getAllProjects());
        const newProject = updatedProject.update({
          id: getNextProjectId(allProjects),
        });

        const targetCategoryId = pendingAddCategoryId;
        if (targetCategoryId === null) {
          return newCategories;
        }

        const addProjectToCategoryTree = (cats: Category[]): Category[] =>
          cats.map((cat) => {
            if (cat.id === targetCategoryId) {
              return new Category({
                id: cat.id,
                name: cat.name,
                color: cat.color,
                projects: [...cat.getProjects(), newProject],
                subcategories: cat.getSubcategories(),
                parentId: cat.parentId,
                level: cat.level,
              });
            }

            return new Category({
              id: cat.id,
              name: cat.name,
              color: cat.color,
              projects: cat.getProjects(),
              subcategories: addProjectToCategoryTree(cat.getSubcategories()),
              parentId: cat.parentId,
              level: cat.level,
            });
          });

        return addProjectToCategoryTree(newCategories);
      }

      return newCategories;
    });

    if (options?.keepModalOpen) {
      setSelectedProject(updatedProject);
    }

    if (!options?.keepModalOpen) {
      handleCloseModal();
    }
  };

  const handleDeleteProject = (projectId: number): void => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      // Recursively delete from category tree
      const deleteCategoryTree = (cat: Category): Category => {
        const hasProject = cat.getProjects().some((p) => p.id === projectId);

        if (hasProject) {
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects().filter((p) => p.id !== projectId),
            subcategories: cat.getSubcategories(),
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        // Recursively check subcategories
        const hasProjectInSubcategory = cat
          .getAllProjects()
          .some((p) => p.id === projectId);

        if (hasProjectInSubcategory) {
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: cat.getSubcategories().map(deleteCategoryTree),
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        return cat;
      };

      setCategories((prevCategories) => prevCategories.map(deleteCategoryTree));
      handleCloseModal();
    }
  };

  const handleAddProject = (categoryId: number): void => {
    const findCategoryById = (cats: Category[], id: number): Category | null => {
      for (const cat of cats) {
        if (cat.id === id) return cat;
        const found = findCategoryById(cat.getSubcategories(), id);
        if (found) return found;
      }
      return null;
    };

    const targetCategory = findCategoryById(categories, categoryId);
    setPendingAddCategoryId(categoryId);
    setSelectedProject(
      createDefaultProject({ color: targetCategory?.color ?? "#3b82f6" }),
    );
    setIsModalOpen(true);
  };

  const handleAddTopLevelCategory = (): void => {
    const categoryName = window.prompt(
      "New top-level category name",
      "New Category",
    );

    if (!categoryName || !categoryName.trim()) return;

    setCategories((prevCategories) => [
      ...prevCategories,
      new Category({
        id: getNextCategoryId(prevCategories),
        name: categoryName.trim(),
        color: getCategoryLevelColor(0),
        projects: [],
        subcategories: [],
        parentId: null,
        level: 0,
      }),
    ]);
  };

  const handleMoveCategory = (
    categoryId: number,
    targetParentId: number | null,
  ): void => {
    setCategories((prevCategories) => {
      if (targetParentId === categoryId) return prevCategories;

      const categoryToMove = findCategoryByIdInTree(prevCategories, categoryId);
      if (!categoryToMove) return prevCategories;

      if (
        targetParentId !== null &&
        categoryToMove.getAllCategories().some((cat) => cat.id === targetParentId)
      ) {
        return prevCategories;
      }

      const targetParent =
        targetParentId === null
          ? null
          : findCategoryByIdInTree(prevCategories, targetParentId);

      if (targetParentId !== null && !targetParent) {
        return prevCategories;
      }

      const removalResult = removeCategoryFromTree(prevCategories, categoryId);
      if (!removalResult.removed) return prevCategories;

      const nextLevel = targetParentId === null ? 0 : (targetParent?.level ?? 0) + 1;
      const rebalancedCategory = rebalanceCategoryTree(
        removalResult.removed,
        targetParentId,
        nextLevel,
      );

      return insertCategoryIntoTree(
        removalResult.categories,
        rebalancedCategory,
        targetParentId,
      );
    });
  };

  const handleDeleteCategory = (categoryId: number): void => {
    setCategories((prevCategories) => {
      const removalResult = removeCategoryFromTree(prevCategories, categoryId);
      if (!removalResult.removed) return prevCategories;
      return removalResult.categories;
    });
  };

  const handleReorderCategory = (
    categoryId: number,
    direction: 'up' | 'down',
  ): void => {
    setCategories((prevCategories) => {
      // Find the category and its siblings
      const updateTree = (cats: Category[]): Category[] => {
        const categoryIndex = cats.findIndex((c) => c.id === categoryId);
        
        if (categoryIndex !== -1) {
          // Found the category at this level
          const targetIndex = direction === 'up' ? categoryIndex - 1 : categoryIndex + 1;
          
          // Check bounds
          if (targetIndex < 0 || targetIndex >= cats.length) {
            return cats;
          }
          
          // Swap
          const newCats = [...cats];
          [newCats[categoryIndex], newCats[targetIndex]] = [
            newCats[targetIndex],
            newCats[categoryIndex],
          ];
          return newCats;
        }
        
        // Recurse into subcategories
        return cats.map((cat) => {
          const updatedSubcategories = updateTree(cat.getSubcategories());
          
          if (updatedSubcategories === cat.getSubcategories()) {
            return cat;
          }
          
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: updatedSubcategories,
            parentId: cat.parentId,
            level: cat.level,
          });
        });
      };
      
      return updateTree(prevCategories);
    });
  };

  const handleMoveTask = (
    sourceProjectId: number,
    movedTask: ProjectTask,
    targetProjectId: number,
  ): void => {
    setCategories((prevCategories) => {
      if (sourceProjectId === targetProjectId) return prevCategories;

      const updateTree = (cat: Category): Category => {
        const updatedSubcategories = cat.getSubcategories().map(updateTree);
        const updatedProjects = cat.getProjects().map((project) => {
          if (project.id === sourceProjectId) {
            return project.update({
              tasks: (project.tasks || []).filter((task) => task.id !== movedTask.id),
            });
          }

          if (project.id === targetProjectId) {
            const existingTasks = project.tasks || [];
            const nextTasks = existingTasks.some((task) => task.id === movedTask.id)
              ? existingTasks.map((task) =>
                  task.id === movedTask.id ? movedTask : task,
                )
              : [...existingTasks, movedTask];

            return project.update({ tasks: nextTasks });
          }

          return project;
        });

        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: updatedProjects,
          subcategories: updatedSubcategories,
          parentId: cat.parentId,
          level: cat.level,
        });
      };

      return prevCategories.map(updateTree);
    });
  };

  const handleMoveProject = (
    projectId: number,
    sourceCategoryId: number,
    targetCategoryId: number,
  ): void => {
    setCategories((prevCategories) => {
      if (sourceCategoryId === targetCategoryId) return prevCategories;

      let projectToMove: Project | null = null;

      const removeFromSource = (cat: Category): Category => {
        const updatedSubcategories = cat.getSubcategories().map(removeFromSource);
        let updatedProjects = cat.getProjects();

        if (cat.id === sourceCategoryId) {
          const found = updatedProjects.find((project) => project.id === projectId);
          if (found) {
            projectToMove = found;
            updatedProjects = updatedProjects.filter(
              (project) => project.id !== projectId,
            );
          }
        }

        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: updatedProjects,
          subcategories: updatedSubcategories,
          parentId: cat.parentId,
          level: cat.level,
        });
      };

      const treeWithoutProject = prevCategories.map(removeFromSource);
      if (!projectToMove) return prevCategories;

      const insertIntoTarget = (cat: Category): Category => {
        const updatedSubcategories = cat.getSubcategories().map(insertIntoTarget);
        let updatedProjects = cat.getProjects();

        if (cat.id === targetCategoryId) {
          const alreadyExists = updatedProjects.some(
            (project) => project.id === projectId,
          );
          updatedProjects = alreadyExists
            ? updatedProjects.map((project) =>
                project.id === projectId ? projectToMove! : project,
              )
            : [...updatedProjects, projectToMove!];
        }

        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: updatedProjects,
          subcategories: updatedSubcategories,
          parentId: cat.parentId,
          level: cat.level,
        });
      };

      return treeWithoutProject.map(insertIntoTarget);
    });
  };

  const handleCategoryColorChange = (categoryId: number, color: string): void => {
    setCategories((prevCategories) => {
      const updateCategoryTree = (cat: Category): Category => {
        const updatedSubcategories = cat
          .getSubcategories()
          .map(updateCategoryTree);

        if (cat.id === categoryId) {
          const updatedProjects = cat.getProjects().map((project) =>
            project.update({ color }),
          );

          return new Category({
            id: cat.id,
            name: cat.name,
            color,
            projects: updatedProjects,
            subcategories: updatedSubcategories,
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: cat.getProjects(),
          subcategories: updatedSubcategories,
          parentId: cat.parentId,
          level: cat.level,
        });
      };

      return prevCategories.map(updateCategoryTree);
    });
  };

  const handleCategoryNameChange = (categoryId: number, name: string): void => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setCategories((prevCategories) => {
      const updateCategoryTree = (cat: Category): Category => {
        const updatedSubcategories = cat
          .getSubcategories()
          .map(updateCategoryTree);

        if (cat.id === categoryId) {
          return new Category({
            id: cat.id,
            name: trimmedName,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: updatedSubcategories,
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: cat.getProjects(),
          subcategories: updatedSubcategories,
          parentId: cat.parentId,
          level: cat.level,
        });
      };

      return prevCategories.map(updateCategoryTree);
    });
  };

  const handleCategoryClientProjectLabelChange = (
    categoryId: number,
    clientProjectId: string,
    shouldAssign: boolean,
  ): void => {
    setCategories((prevCategories) => {
      const updateCategoryTree = (
        category: Category,
        inTargetSubtree: boolean,
      ): Category => {
        const isTargetCategory = category.id === categoryId;
        const shouldUpdateProjects = inTargetSubtree || isTargetCategory;

        const updatedProjects = category.getProjects().map((project) => {
          if (!shouldUpdateProjects) {
            return project;
          }

          const currentClientProjectIds = project.clientProjectIds || [];
          const hasLabel = currentClientProjectIds.includes(clientProjectId);

          if (shouldAssign && hasLabel) {
            return project;
          }

          if (!shouldAssign && !hasLabel) {
            return project;
          }

          const nextClientProjectIds = shouldAssign
            ? [...currentClientProjectIds, clientProjectId]
            : currentClientProjectIds.filter((id) => id !== clientProjectId);

          return project.update({ clientProjectIds: nextClientProjectIds });
        });

        const updatedSubcategories = category
          .getSubcategories()
          .map((subcategory) =>
            updateCategoryTree(subcategory, shouldUpdateProjects),
          );

        return new Category({
          id: category.id,
          name: category.name,
          color: category.color,
          projects: updatedProjects,
          subcategories: updatedSubcategories,
          parentId: category.parentId,
          level: category.level,
        });
      };

      return prevCategories.map((category) => updateCategoryTree(category, false));
    });
  };

  const handleProjectStatusChange = (updatedProject: Project): void => {
    setCategories((prevCategories) => {
      // Recursively update status in category tree
      const updateStatusInTree = (cat: Category): Category => {
        // Check if this category contains the project
        const projectExists = cat
          .getProjects()
          .some((p) => p.id === updatedProject.id);

        if (projectExists) {
          // Update the project in this category
          const updatedProjects = cat
            .getProjects()
            .map((p) => (p.id === updatedProject.id ? updatedProject : p));
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: updatedProjects,
            subcategories: cat.getSubcategories(),
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        // Recursively check subcategories
        const hasProjectInSubcategory = cat
          .getAllProjects()
          .some((p) => p.id === updatedProject.id);

        if (hasProjectInSubcategory) {
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: cat.getSubcategories().map(updateStatusInTree),
            parentId: cat.parentId,
            level: cat.level,
          });
        }

        return cat;
      };

      return prevCategories.map(updateStatusInTree);
    });
  };

  // --- Helpers for tree traversal ---
  const findParentOfCategory = (categoryId: number, cats: Category[]): Category | null => {
    for (const cat of cats) {
      if (cat.getSubcategories().some((s) => s.id === categoryId)) return cat;
      const found = findParentOfCategory(categoryId, cat.getSubcategories());
      if (found) return found;
    }
    return null;
  };

  const findParentOfProject = (projectId: number, cats: Category[]): Category | null => {
    for (const cat of cats) {
      if (cat.getProjects().some((p) => p.id === projectId)) return cat;
      const found = findParentOfProject(projectId, cat.getSubcategories());
      if (found) return found;
    }
    return null;
  };

  const getNextCategoryId = (cats: Category[]): number => {
    const allIds = cats.flatMap((c) => c.getAllCategories()).map((c) => c.id ?? 0);
    return Math.max(0, ...allIds) + 1;
  };

  const findCategoryByIdInTree = (
    cats: Category[],
    categoryId: number,
  ): Category | null => {
    for (const cat of cats) {
      if (cat.id === categoryId) return cat;
      const found = findCategoryByIdInTree(cat.getSubcategories(), categoryId);
      if (found) return found;
    }

    return null;
  };

  const rebalanceCategoryTree = (
    category: Category,
    parentId: number | null,
    level: number,
  ): Category =>
    new Category({
      id: category.id,
      name: category.name,
      color: category.color,
      projects: category.getProjects(),
      subcategories: category
        .getSubcategories()
        .map((subcat) => rebalanceCategoryTree(subcat, category.id, level + 1)),
      parentId,
      level,
    });

  const insertCategoryIntoTree = (
    cats: Category[],
    categoryToInsert: Category,
    parentId: number | null,
  ): Category[] => {
    if (parentId === null) {
      return [...cats, categoryToInsert];
    }

    return cats.map((cat) => {
      if (cat.id === parentId) {
        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: cat.getProjects(),
          subcategories: [...cat.getSubcategories(), categoryToInsert],
          parentId: cat.parentId,
          level: cat.level,
        });
      }

      return new Category({
        id: cat.id,
        name: cat.name,
        color: cat.color,
        projects: cat.getProjects(),
        subcategories: insertCategoryIntoTree(
          cat.getSubcategories(),
          categoryToInsert,
          parentId,
        ),
        parentId: cat.parentId,
        level: cat.level,
      });
    });
  };

  const removeCategoryFromTree = (
    cats: Category[],
    categoryId: number,
  ): { categories: Category[]; removed: Category | null } => {
    let removed: Category | null = null;

    const categoriesAfterRemoval = cats.flatMap((cat) => {
      if (cat.id === categoryId) {
        removed = cat;
        return [];
      }

      const childResult = removeCategoryFromTree(cat.getSubcategories(), categoryId);
      if (childResult.removed) {
        removed = childResult.removed;
        return [
          new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: childResult.categories,
            parentId: cat.parentId,
            level: cat.level,
          }),
        ];
      }

      return [cat];
    });

    return { categories: categoriesAfterRemoval, removed };
  };

  // --- Convert category → project ---
  const handleConvertCategoryToProject = (categoryId: number): void => {
    setCategories((prevCategories) => {
      const allCats = prevCategories.flatMap((c) => c.getAllCategories());
      const category = allCats.find((c) => c.id === categoryId);
      if (!category) return prevCategories;

      const parent = findParentOfCategory(categoryId, prevCategories);
      if (!parent) return prevCategories; // root categories can't be converted

      const allProjects = category.getAllProjects();

      // Merge effort breakdowns
      const mergedEffort: EffortBreakdown = {
        [WorkCategory.DEVELOPMENT]: 0,
        [WorkCategory.IA]: 0,
        [WorkCategory.PROJECT_MANAGEMENT]: 0,
        [WorkCategory.CONTENT_CREATION]: 0,
        [WorkCategory.COG_SCI]: 0,
      };
      for (const p of allProjects) {
        for (const key of Object.values(WorkCategory)) {
          mergedEffort[key] = (mergedEffort[key] || 0) + (p.effortBreakdown[key] || 0);
        }
      }

      // Compute date range from sub-projects
      const dates = allProjects.flatMap((p) =>
        [p.startDate, p.endDate].filter(Boolean) as Date[]
      );
      const startDate = dates.length > 0
        ? new Date(Math.min(...dates.map((d) => d.getTime())))
        : new Date();
      const endDate = dates.length > 0
        ? new Date(Math.max(...dates.map((d) => d.getTime())))
        : new Date();

      // Pick most significant status
      const statuses = allProjects.map((p) => p.status);
      const status = statuses.includes(Status.IN_PROGRESS)
        ? Status.IN_PROGRESS
        : statuses.includes(Status.PLANNED)
        ? Status.PLANNED
        : statuses[0] ?? Status.PLANNED;

      const newId = getNextProjectId(prevCategories.flatMap((c) => c.getAllProjects()));
      const mergedTasks = allProjects.flatMap((p) => p.tasks || []);
      const newProject = new Project({
        id: newId,
        name: category.name,
        startDate,
        endDate,
        color: category.color,
        effortBreakdown: mergedEffort,
        tasks: mergedTasks,
      });
      newProject.status = status;

      // Replace category with project inside its parent
      const replaceInTree = (cats: Category[]): Category[] =>
        cats.map((cat) => {
          if (cat.id === parent.id) {
            return new Category({
              id: cat.id,
              name: cat.name,
              color: cat.color,
              projects: [...cat.getProjects(), newProject],
              subcategories: cat.getSubcategories().filter((s) => s.id !== categoryId),
              parentId: cat.parentId,
              level: cat.level,
            });
          }
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: replaceInTree(cat.getSubcategories()),
            parentId: cat.parentId,
            level: cat.level,
          });
        });

      return replaceInTree(prevCategories);
    });
  };

  // --- Convert project → category ---
  const handleConvertProjectToCategory = (projectId: number): void => {
    setCategories((prevCategories) => {
      const parent = findParentOfProject(projectId, prevCategories);
      if (!parent) return prevCategories;

      const project = parent.getProjects().find((p) => p.id === projectId);
      if (!project) return prevCategories;

      const newCatId = getNextCategoryId(prevCategories);
      const newCategory = new Category({
        id: newCatId,
        name: project.name,
        color: project.color,
        projects: [project],
        subcategories: [],
        parentId: parent.id,
        level: (parent.level ?? 0) + 1,
      });

      const replaceInTree = (cats: Category[]): Category[] =>
        cats.map((cat) => {
          if (cat.id === parent.id) {
            return new Category({
              id: cat.id,
              name: cat.name,
              color: cat.color,
              projects: cat.getProjects().filter((p) => p.id !== projectId),
              subcategories: [...cat.getSubcategories(), newCategory],
              parentId: cat.parentId,
              level: cat.level,
            });
          }
          return new Category({
            id: cat.id,
            name: cat.name,
            color: cat.color,
            projects: cat.getProjects(),
            subcategories: replaceInTree(cat.getSubcategories()),
            parentId: cat.parentId,
            level: cat.level,
          });
        });

      return replaceInTree(prevCategories);
    });
  };

  const handleImportCSV = async (file: File): Promise<void> => {
    try {
      const importedCategories = await readCSVFile(file);
      setCategories(importedCategories);
    } catch (error) {
      console.error("Failed to import CSV:", error);
      alert(
        `Failed to import CSV: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleOpenBackupModal = (): void => {
    setIsBackupModalOpen(true);
  };

  const handleCloseBackupModal = (): void => {
    setIsBackupModalOpen(false);
  };

  const handleLoadScheduleWithClientProjects = (
    loadedCategories: Category[],
    loadedClientProjects: ClientProject[],
  ): void => {
    setCategories(loadedCategories);
    setClientProjects(loadedClientProjects);
  };

  const handleCreateClientProject = (name: string): boolean => {
    const normalized = name.trim();
    if (!normalized) return false;

    const exists = clientProjects.some(
      (clientProject) =>
        clientProject.name.trim().toLowerCase() === normalized.toLowerCase(),
    );

    if (exists) return false;

    setClientProjects((prev) => [
      ...prev,
      {
        id: `client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: normalized,
      },
    ]);
    return true;
  };

  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const addDays = (date: Date, days: number): Date => {
    const shifted = new Date(date);
    shifted.setDate(shifted.getDate() + days);
    return shifted;
  };

  const handleShiftCategoryTiming = ({
    categoryId,
    boundary,
    targetDate,
  }: CategoryTimingShiftPayload): void => {
    setCategories((prevCategories) => {
      const includedStatuses = [
        Status.BACKLOG,
        Status.UNDER_DISCUSSION,
        Status.PLANNED,
        Status.IN_PROGRESS,
      ];

      const findCategoryById = (cats: Category[]): Category | undefined => {
        for (const cat of cats) {
          if (cat.id === categoryId) return cat;
          const nested = findCategoryById(cat.getSubcategories());
          if (nested) return nested;
        }
        return undefined;
      };

      const targetCategory = findCategoryById(prevCategories);
      if (!targetCategory) return prevCategories;

      const timestamps = targetCategory
        .getAllProjects()
        .filter((project) => includedStatuses.includes(project.status))
        .flatMap((project) => {
          const dates: Date[] = [];
          if (project.startDate) dates.push(project.startDate);
          if (project.endDate) dates.push(project.endDate);
          return dates;
        })
        .map((date) => date.getTime());

      if (timestamps.length === 0) {
        return prevCategories;
      }

      const anchorTime =
        boundary === "start"
          ? Math.min(...timestamps)
          : Math.max(...timestamps);
      const targetTime = parseLocalDate(targetDate).getTime();
      const millisecondsPerDay = 1000 * 60 * 60 * 24;
      const deltaDays = Math.round(
        (targetTime - anchorTime) / millisecondsPerDay,
      );

      if (deltaDays === 0) {
        return prevCategories;
      }

      const shiftCategoryTree = (
        cat: Category,
        inTargetSubtree: boolean,
      ): Category => {
        const isTargetCategory = cat.id === categoryId;
        const shouldShiftHere = inTargetSubtree || isTargetCategory;

        const shiftedProjects = cat.getProjects().map((project) => {
          if (!shouldShiftHere || !includedStatuses.includes(project.status)) {
            return project;
          }

          const updates: Partial<{
            startDate: Date;
            endDate: Date;
            tasks: ProjectTask[];
          }> = {};
          let nextStartDate = project.startDate;
          let nextEndDate = project.endDate;
          if (project.startDate) {
            nextStartDate = addDays(project.startDate, deltaDays);
            updates.startDate = nextStartDate;
          }
          if (project.endDate) {
            nextEndDate = addDays(project.endDate, deltaDays);
            updates.endDate = nextEndDate;
          }

          const nextProjectSnapshot = project.update({
            startDate: updates.startDate,
            endDate: updates.endDate,
          });
          nextProjectSnapshot.status = project.status;

          const adjustedTasks = shiftTasksForDateChange(
            project,
            nextProjectSnapshot,
            nextStartDate,
            nextEndDate,
          );
          updates.tasks = adjustedTasks;

          return Object.keys(updates).length > 0
            ? project.update(updates)
            : project;
        });

        const shiftedSubcategories = cat
          .getSubcategories()
          .map((subcat) => shiftCategoryTree(subcat, shouldShiftHere));

        return new Category({
          id: cat.id,
          name: cat.name,
          color: cat.color,
          projects: shiftedProjects,
          subcategories: shiftedSubcategories,
          parentId: cat.parentId,
          level: cat.level,
        });
      };

      return prevCategories.map((cat) => shiftCategoryTree(cat, false));
    });
  };

  return (
    // Show a blank screen while checking session
    authLoading ? null : !authUser ? ( // Show login page if not authenticated
      <LoginPage
        onLogin={setAuthUser}
        errorParam={new URLSearchParams(window.location.search).get("error")}
      />
    ) : (
      <div className="App">
        <header className="app-header">
          <h1>Company Project Schedule</h1>
          <div className="app-header-user">
            <span className="app-header-username">{authUser.displayName}</span>
            <span className="app-header-role">{formatRole(authUser.role)}</span>
            <button
              className="app-header-logout"
              onClick={async () => {
                await logout();
                setAuthUser(null);
              }}
            >
              Sign out
            </button>
          </div>
        </header>
        <ProjectSchedule
          schedule={schedule}
          clientProjects={clientProjects}
          onCreateClientProject={canEdit ? handleCreateClientProject : undefined}
          onCategoryClientProjectLabelChange={
            canEdit ? handleCategoryClientProjectLabelChange : undefined
          }
          onProjectClick={handleProjectClick}
          onAddProject={canEdit ? handleAddProject : () => {}}
          onAddTopLevelCategory={
            canEdit ? handleAddTopLevelCategory : undefined
          }
          onCategoryColorChange={
            canEdit ? handleCategoryColorChange : undefined
          }
          onCategoryNameChange={canEdit ? handleCategoryNameChange : undefined}
          onDeleteCategory={canEdit ? handleDeleteCategory : undefined}
          onReorderCategory={canEdit ? handleReorderCategory : undefined}
          onImportCSV={canEdit ? handleImportCSV : async () => {}}
          onProjectStatusChange={
            canEdit ? handleProjectStatusChange : undefined
          }
          onOpenBackup={canEdit ? handleOpenBackupModal : undefined}
          onConvertCategoryToProject={
            canEdit ? handleConvertCategoryToProject : undefined
          }
          onConvertProjectToCategory={
            canEdit ? handleConvertProjectToCategory : undefined
          }
          onMoveCategory={canEdit ? handleMoveCategory : undefined}
          onMoveProject={canEdit ? handleMoveProject : undefined}
          onShiftCategoryTiming={
            canEdit ? handleShiftCategoryTiming : undefined
          }
          onCapacityConfigChange={handleCapacityConfigChange}
        />
        <ProjectEditModal
          project={selectedProject}
          availableClientProjects={clientProjects}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveProject}
          onDelete={handleDeleteProject}
          availableProjects={schedule.getAllProjects()}
          onMoveTask={handleMoveTask}
        />
        <BackupModal
          isOpen={isBackupModalOpen}
          onClose={handleCloseBackupModal}
          currentCategories={categories}
          currentClientProjects={clientProjects}
          onLoadSchedule={handleLoadScheduleWithClientProjects}
        />
      </div>
    )
  );
};

export default App;
